import React from 'react';
import { 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  Typography,
  Box
} from '@mui/material';
import { CheckCircleOutline, ErrorOutline, InfoOutlined, WarningAmber } from '@mui/icons-material';

// Tipos de mensagem suportados
export type MessageType = 'success' | 'error' | 'warning' | 'info';

interface MessageModalProps {
  open: boolean;
  onClose: () => void;
  message: string;
  title?: string;
  type?: MessageType;
}

/**
 * Componente MessageModal
 * 
 * Modal que exibe mensagens com diferentes tipos (sucesso, erro, aviso, informação).
 * Força o usuário a clicar no botão para confirmar que leu a mensagem.
 * 
 * @param open - Estado que controla se o modal está aberto ou fechado
 * @param onClose - Função chamada quando o usuário fecha o modal
 * @param message - Mensagem a ser exibida no modal
 * @param title - Título opcional do modal (default baseado no tipo)
 * @param type - Tipo de mensagem (success, error, warning, info)
 */
export function MessageModal({ 
  open, 
  onClose, 
  message, 
  title, 
  type = 'success' 
}: MessageModalProps) {
  // Função vazia para evitar o fechamento do modal ao clicar fora
  const handleDialogClose = (_: {}, reason: string) => {
    // Não faz nada, impedindo o fechamento do modal
    // O modal só pode ser fechado clicando no botão OK
  };
  
  // Configurações baseadas no tipo de mensagem
  const getModalConfig = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckCircleOutline sx={{ fontSize: 60 }} />,
          color: 'success.main',
          headerColor: 'primary.main',
          headerText: title || 'Sucesso!',
          buttonColor: 'primary'
        };
      case 'error':
        return {
          icon: <ErrorOutline sx={{ fontSize: 60 }} />,
          color: 'error.main',
          headerColor: 'error.main',
          headerText: title || 'Erro',
          buttonColor: 'error'
        };
      case 'warning':
        return {
          icon: <WarningAmber sx={{ fontSize: 60 }} />,
          color: 'warning.main',
          headerColor: 'warning.main',
          headerText: title || 'Aviso',
          buttonColor: 'warning'
        };
      case 'info':
      default:
        return {
          icon: <InfoOutlined sx={{ fontSize: 60 }} />,
          color: 'info.main',
          headerColor: 'info.main',
          headerText: title || 'Informação',
          buttonColor: 'info'
        };
    }
  };

  const config = getModalConfig();

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      aria-labelledby="message-dialog-title"
      aria-describedby="message-dialog-description"
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          zIndex: 9999,
        }
      }}
    >
      <DialogTitle 
        id="message-dialog-title" 
        sx={{ bgcolor: config.headerColor, color: 'white', pb: 1, textAlign: 'center' }}
      >
        {config.headerText}
      </DialogTitle>
      
      <DialogContent sx={{ pt: 4, pb: 2 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Box sx={{ color: config.color }}>
            {config.icon}
          </Box>
          
          <Typography 
            variant="body1" 
            id="message-dialog-description" 
            align="center"
            textAlign="justify"
          >
            {message}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ pb: 2, px: 3 }}>
        <Button 
          onClick={onClose}
          variant="contained"
          color={config.buttonColor as "primary" | "error" | "warning" | "info"}
          size="large"
          fullWidth
          sx={{ 
            borderRadius: '8px',
            fontWeight: 'bold',
            py: 1
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
} 